import { ReactNode } from "react";
import * as React from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { get } from "lodash";

export type OptionType = {
  value: string | number;
  label: string;
};
type SwitchFormFieldProps = {
  name: string;
  label: string | ReactNode;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps;
  fullWidth?: boolean;
  tooltip?: string;
};

export type RhfSwitchFormFieldProps<T extends FieldValues> =
  SwitchFormFieldProps & UseControllerProps<T>;

export default function SwitchFormField<T extends FieldValues>({
  name,
  label,
  control,
  required = false,
  disabled = false,
  fullWidth = false,
  tooltip = "",
  sx
}: RhfSwitchFormFieldProps<T>) {
  const {
    field,
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      required: {
        value: required,
        message: `${label} is required`
      }
    }
  });
  const { value, ...restField } = field;

  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  return (
    <FormControl
      disabled={disabled}
      required={required}
      fullWidth={fullWidth}
      error={Boolean(errorMessage)}
      sx={sx}
    >
      <FormControlLabel
        control={
          <Tooltip title={tooltip}>
            <Switch checked={value} color="primary" {...restField} />
          </Tooltip>
        }
        label={label}
      />
      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
